/**
 * Alpine.js Language Selector Component
 *
 * Provides a language picker for the settings page with desktop dropdown
 * and mobile bottom sheet.
 */

/**
 * Creates a language selector component
 * @param {string} initialLang - Initial locale (e.g., 'en_US')
 * @param {Object} availableLanguages - Map of locale to language info
 * @returns {Object} Alpine.js component data
 */
export function languageSelector(initialLang, availableLanguages) {
    // Preserve PHP-supplied order — LocalizationService::getAvailableUILanguages()
    // orders locales by native name with script grouping.
    const languages = Object.entries(availableLanguages || {}).map(([locale, info]) => ({
        locale,
        name: info.name,
        native: info.native,
        rtl: info.rtl
    }));

    return {
        selectedLang: initialLang || 'en_US',
        sheetOpen: false,
        isSaving: false,
        languages,

        get displayLabel() {
            const lang = this.languages.find(l => l.locale === this.selectedLang);
            return lang ? `${lang.native} (${lang.name})` : (window.t ? window.t('settings.select_language') : 'Select language');
        },

        openSheet() {
            this.sheetOpen = true;
            this.$nextTick(() => {
                window.LucideIcons?.initialize(this.$el);
            });
        },

        selectLang(locale) {
            this.selectedLang = locale;
            this.sheetOpen = false;
            this.saveLang();
        },

        saveLang() {
            const csrfToken = document.querySelector('meta[name="csrf_token"]')?.content ||
                             document.querySelector('input[name="csrf_token"]')?.value;
            if (!csrfToken) return;

            this.isSaving = true;

            const formData = new FormData();
            formData.append('field', 'lang');
            formData.append('value', this.selectedLang);
            formData.append('csrf_token', csrfToken);

            fetch('/app/settings/field/update', {
                method: 'POST',
                body: formData,
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    // Reload the page to apply the new language
                    window.location.reload();
                } else {
                    this.isSaving = false;
                    const errorMsg = data.error_msg || (window.t ? window.t('settings.language_update_failed') : 'Failed to update language');
                    alert(errorMsg);
                }
            })
            .catch(() => {
                this.isSaving = false;
                const errorMsg = window.t ? window.t('settings.language_update_failed') : 'Failed to update language';
                alert(errorMsg);
            });
        }
    };
}
